import React, { useState } from 'react';
import { TagContainer } from '../TagContainer';
import { TagSelectionModal } from './TagSelectionModal';
import { useTags } from '../../hooks/useTags';
import EditableField from '../shared/EditableField';
import CategorySelector from '../shared/CategorySelector';
import { IconButton, Checkbox, Button } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from '@mui/icons-material/Add';

export const ReceiptItemRow = ({
  item,
  isMobile,
  onEdit,
  onDelete,
  isSplitting,
  splitItems,
  splitColors,
  splits,
  handleItemSelect,
  expandedItems,
  toggleItemExpand,
  getEffectiveValue,
  isFieldEdited,
  parseCurrencyString,
}) => {
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const { tags, addTag, removeTag, isLoading } = useTags({ itemId: item.item_id });

  const handleAddTag = async (tagId) => {
    try {
      await addTag(tagId);
      setIsTagModalOpen(false);
    } catch (error) {
      console.error('Failed to add tag:', error);
    }
  };

  const handleRemoveTag = async (tag) => {
    await removeTag(tag.tag_id);
  };

  const handleTagClick = (tag) => {
    // Handle tag click - could be used for filtering or other actions
    console.log('Tag clicked:', tag);
  };

  const handleItemNameChange = (value) => {
    onEdit(item.item_id, { field: 'item_name', value });
  };

  const handleItemPriceChange = (value) => {
    onEdit(item.item_id, { field: 'item_price', value });
  };

  const handleCategoryChange = (highLevel, detailed) => {
    onEdit(item.item_id, [
      { field: 'high_level_category', value: highLevel },
      { field: 'detailed_category', value: detailed }
    ]);
  };

  const renderTagContainer = (isMobileView) => (
    <div className={`flex items-center ${isMobileView ? 'w-full' : 'w-full'}`}>
      <TagContainer
        tags={tags || []}
        maxVisible={isMobileView ? 5 : 4}
        onAddTag={() => setIsTagModalOpen(true)}
        onTagClick={handleTagClick}
        onRemoveTag={handleRemoveTag}
        variant="inline"
        isMobile={isMobileView}
        showAddText={isMobileView}
        mobileTouchFriendly={isMobileView}
      />
    </div>
  );

  if (isMobile) {
    return (
      <>
        <tr 
          className={`border-b border-gray-700 transition-all duration-300 ease-custom hover:bg-gray-700 ${item.is_maybe_discount ? 'text-green-400' : ''}`}
          onClick={() => toggleItemExpand(item.item_id)}
        >
          {isSplitting && (
            <td className="text-center" style={{ width: '10%' }}>
              <Checkbox
                checked={splitItems[item.item_id] !== undefined}
                onChange={(e) => {
                  e.stopPropagation();
                  handleItemSelect(item.item_id);
                }}
                onClick={(e) => e.stopPropagation()}
                disabled={splitItems[item.item_id] !== undefined && splitItems[item.item_id] !== splits.length - 1}
                style={{
                  color: splitItems[item.item_id] !== undefined ? splitColors[splitItems[item.item_id]] : '',
                }}
              />
            </td>
          )}
          <td className="p-3 text-left" style={{ width: isSplitting ? '55%' : '65%' }}>
            <div className="flex items-center">
              <span className="mr-2 flex-shrink-0">{item.category_emoji}</span>
              <span className="break-words" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {getEffectiveValue(item, 'item_name')}
              </span>
            </div>
          </td>
          <td className="p-3 text-right" style={{ width: '25%' }}>
            {item.is_maybe_discount ? '-' : ''}${Math.abs(parseCurrencyString(getEffectiveValue(item, 'item_price') || 0)).toFixed(2)}
          </td>
          <td className="p-3 text-center" style={{ width: '10%' }}>
            <div className="flex justify-center items-center h-full">
              {expandedItems[item.item_id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </div>
          </td>
        </tr>
        {expandedItems[item.item_id] && (
          <tr className="bg-gray-800">
            <td colSpan="4" className="p-0">
              <div className="p-3 text-sm">
                {/* Item Name */}
                <div className="flex items-center py-2 border-b border-gray-700">
                  <span className="text-gray-400 flex-shrink-0 w-20">Name:</span>
                  <div className="flex-1 min-w-0">
                    <EditableField
                      value={getEffectiveValue(item, 'item_name')}
                      onSave={handleItemNameChange}
                      label="Item Name"
                      isEdited={isFieldEdited(item, 'item_name')}
                      fullWidth
                    />
                  </div>
                </div>

                {/* Price */}
                <div className="flex items-center py-2 border-b border-gray-700">
                  <span className="text-gray-400 flex-shrink-0 w-20">Price:</span>
                  <div className="flex-1 min-w-0">
                    <EditableField
                      value={getEffectiveValue(item, 'item_price')}
                      onSave={handleItemPriceChange}
                      label="Price"
                      type="number"
                      isEdited={isFieldEdited(item, 'item_price')}
                      className={item.is_maybe_discount ? 'text-green-400' : ''}
                      fullWidth
                    />
                  </div>
                </div>

                {/* Category */}
                <div className="flex items-center py-2 border-b border-gray-700">
                  <span className="text-gray-400 flex-shrink-0 w-20">Category:</span>
                  <div className="flex-1 min-w-0">
                    <CategorySelector
                      highLevelCategory={getEffectiveValue(item, 'high_level_category')}
                      detailedCategory={getEffectiveValue(item, 'detailed_category')}
                      onSave={handleCategoryChange}
                    />
                  </div>
                </div>

                {/* Tags */}
                <div className="flex items-center py-2 border-b border-gray-700">
                  <span className="text-gray-400 flex-shrink-0 w-20">Tags:</span>
                  <div className="flex-1 min-w-0">
                    {renderTagContainer(true)}
                  </div>
                </div>

                {/* Delete Button */}
                <div className="flex justify-end mt-4">
                  <Button
                    onClick={() => onDelete(item.item_id)}
                    color="error"
                    variant="outlined"
                    startIcon={<DeleteIcon />}
                    size="small"
                  >
                    Delete Item
                  </Button>
                </div>

                {/* Discount indicator */}
                {item.is_maybe_discount && (
                  <div className="mt-2 text-green-400 text-sm font-medium text-center">
                    Discount item
                  </div>
                )}
              </div>
            </td>
          </tr>
        )}
        <TagSelectionModal
          open={isTagModalOpen}
          onClose={() => setIsTagModalOpen(false)}
          onSelectTag={handleAddTag}
          isLoading={isLoading}
        />
      </>
    );
  }

  return (
    <>
      <tr 
        className={`
          border-b border-gray-700 last:border-b-0
          transition-all duration-300 ease-out
          hover:bg-gray-700 group
          ${item.is_maybe_discount ? 'text-green-400' : ''}
        `}
      >
        <td className={`transition-all duration-300 ease-out ${isSplitting ? 'w-16' : 'w-0'}`}>
          {isSplitting && (
            <div className="transition-all duration-300 ease-out transform">
              <Checkbox
                checked={splitItems[item.item_id] !== undefined}
                onChange={() => handleItemSelect(item.item_id)}
                disabled={splitItems[item.item_id] !== undefined && splitItems[item.item_id] !== splits.length - 1}
                style={{
                  color: splitItems[item.item_id] !== undefined ? splitColors[splitItems[item.item_id]] : '',
                }}
              />
            </div>
          )}
        </td>
        <td className="p-3 text-left overflow-hidden">
          <div className="truncate">
            <EditableField
              value={getEffectiveValue(item, 'item_name')}
              onSave={handleItemNameChange}
              label="Item Name"
              isEdited={isFieldEdited(item, 'item_name')}
            />
          </div>
        </td>
        <td className="p-3 text-left">
          {renderTagContainer(false)}
        </td>
        <td className="p-3 text-left">
          <CategorySelector
            highLevelCategory={getEffectiveValue(item, 'high_level_category')}
            detailedCategory={getEffectiveValue(item, 'detailed_category')}
            onSave={handleCategoryChange}
          />
        </td>
        <td className="p-3 whitespace-nowrap">
          <div className="flex justify-end">
            <EditableField
              value={getEffectiveValue(item, 'item_price')}
              onSave={handleItemPriceChange}
              label="Price"
              type="number"
              isEdited={isFieldEdited(item, 'item_price')}
              className={item.is_maybe_discount ? 'text-green-400' : ''}
            />
          </div>
        </td>
        <td className="p-3 text-center">
          <IconButton
            onClick={() => onDelete(item.item_id)}
            color="error"
            size="small"
            className="opacity-0 group-hover:opacity-100 transition-opacity duration-200"
          >
            <DeleteIcon />
          </IconButton>
        </td>
      </tr>
      <TagSelectionModal
        open={isTagModalOpen}
        onClose={() => setIsTagModalOpen(false)}
        onSelectTag={handleAddTag}
        isLoading={isLoading}
      />
    </>
  );
}; 